/**
 * Bale QC / HVI Entry Page
 * ──────────────────────────────────────────────────────────────────────────────
 * Select Mill Lot No → system pulls all bales → QC enters HVI parameters
 * → approves / holds / rejects each bale.
 * Lot-level averages & CVs auto-recompute after every entry.
 *
 * No re-entry of supplier/variety/origin — all auto-filled from Mill Lot.
 */
import { useState } from 'react'
import {
  App, Button, Card, Col, Descriptions, Drawer, Form, Input, InputNumber,
  Popconfirm, Row, Select, Space, Table, Tag, Typography, Progress, Alert,
} from 'antd'
import {
  CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined,
  PlusOutlined, HistoryOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { millLotApi, hviApi } from '@/features/baleManagement/api'
import { MillLotCard } from './MillLotPage'
import PageHeader from '@/shared/components/PageHeader'
import dayjs from 'dayjs'

const { Text } = Typography

const QC_COLORS: Record<string, string> = {
  PENDING: 'orange', APPROVED: 'green', HOLD: 'gold', REJECTED: 'red',
}

export default function BaleQCPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [bulkForm] = Form.useForm()
  const [activeLot, setActiveLot] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)
  const [selectedBale, setSelectedBale] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)
  const [historyBale, setHistoryBale] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)
  const [bulkOpen, setBulkOpen] = useState(false)

  // Bales in this lot
  const { data: bales = [], isLoading: balesLoading } = useQuery({
    queryKey: ['lot-bales', activeLot?.id],
    queryFn: () => millLotApi.getBales(activeLot.id),
    enabled: !!activeLot?.id,
  })

  // HVI history for selected bale
  const { data: hviHistory = [] } = useQuery({
    queryKey: ['hvi-history', historyBale?.id],
    queryFn: () => hviApi.history(historyBale.id),
    enabled: !!historyBale?.id,
  })

  const enterHVI = useMutation({
    mutationFn: ({ bale_id, data }: any) => hviApi.enter(bale_id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lot-bales'] })
      qc.invalidateQueries({ queryKey: ['mill-lots'] })
      message.success('HVI entered — lot averages updated')
      setSelectedBale(null)
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const approveHVI = useMutation({
    mutationFn: ({ result_id, data }: any) => hviApi.approve(result_id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lot-bales'] })
      qc.invalidateQueries({ queryKey: ['mill-lots'] })
      message.success('Status updated')
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const bulkHVI = useMutation({
    mutationFn: ({ lot_id, rows }: any) => millLotApi.bulkHvi(lot_id, rows),
    onSuccess: (results) => {
      qc.invalidateQueries({ queryKey: ['lot-bales'] })
      qc.invalidateQueries({ queryKey: ['mill-lots'] })
      message.success(`${results.length} HVI results entered — lot averages recalculated`)
      setBulkOpen(false)
      bulkForm.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const baleList = Array.isArray(bales) ? bales : []
  const pending = baleList.filter((b: any) => b.qc_status === 'PENDING').length
  const approved = baleList.filter((b: any) => b.qc_status === 'APPROVED').length
  const hold = baleList.filter((b: any) => b.qc_status === 'HOLD').length
  const rejected = baleList.filter((b: any) => b.qc_status === 'REJECTED').length

  const columns = [
    {
      title: 'Bale No', dataIndex: 'bale_no', key: 'bale_no', width: 140,
      render: (v: string) => <Text code style={{ fontSize: 12 }}>{v}</Text>,
    },
    { title: 'Location', dataIndex: 'location', key: 'location', width: 120 },
    { title: 'Wt (kg)', dataIndex: 'weight_kg', key: 'wt', width: 80, align: 'right' as const,
      render: (v: any) => Number(v || 0).toFixed(1) },
    { title: 'Mic', dataIndex: 'micronaire', key: 'mic', width: 70, align: 'right' as const,
      render: (v: any) => v ? <Text type={Number(v) >= 3.5 && Number(v) <= 5.2 ? 'success' : 'warning'}>{Number(v).toFixed(2)}</Text> : <Text type="secondary">—</Text> },
    { title: 'UHML (mm)', dataIndex: 'uhml_mm', key: 'uhml', width: 90, align: 'right' as const,
      render: (v: any) => v ? Number(v).toFixed(1) : <Text type="secondary">—</Text> },
    { title: 'Str (g/tex)', dataIndex: 'strength_g_tex', key: 'str', width: 90, align: 'right' as const,
      render: (v: any) => v ? Number(v).toFixed(1) : <Text type="secondary">—</Text> },
    { title: 'Rd', dataIndex: 'rd_value', key: 'rd', width: 60, align: 'right' as const,
      render: (v: any) => v ? Number(v).toFixed(1) : <Text type="secondary">—</Text> },
    { title: '+b', dataIndex: 'plus_b', key: 'pb', width: 60, align: 'right' as const,
      render: (v: any) => v ? Number(v).toFixed(1) : <Text type="secondary">—</Text> },
    { title: 'Trash%', dataIndex: 'trash_pct', key: 'trash', width: 70, align: 'right' as const,
      render: (v: any) => v ? Number(v).toFixed(1) : <Text type="secondary">—</Text> },
    { title: 'Band', dataIndex: 'category_band', key: 'band', width: 80,
      render: (v: string) => v ? <Tag color="blue">{v}</Tag> : <Text type="secondary">—</Text> },
    { title: 'QC Status', dataIndex: 'qc_status', key: 'qc', width: 100,
      render: (v: string) => <Tag color={QC_COLORS[v] || 'default'}>{v || 'PENDING'}</Tag> },
    {
      title: 'Actions', key: 'actions', width: 160,
      render: (_: unknown, r: any) => (
        <Space size={4}>
          <Button size="small" icon={<PlusOutlined />}
            onClick={() => { setSelectedBale(r); form.resetFields() }}>HVI</Button>
          <Button size="small" icon={<HistoryOutlined />}
            onClick={() => setHistoryBale(r)}>Log</Button>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Bale QC / HVI Entry"
        subtitle="Select Mill Lot → enter HVI per bale → approve / hold / reject"
        extra={activeLot && (
          <Button icon={<PlusOutlined />} onClick={() => setBulkOpen(true)}>
            Bulk HVI Entry
          </Button>
        )}
      />

      {/* Mill Lot auto-fill */}
      <div style={{ marginBottom: 16 }}>
        <MillLotCard onLoaded={setActiveLot} />
      </div>

      {activeLot && (
        <>
          {/* Stats */}
          <Row gutter={12} style={{ marginBottom: 16 }}>
            {[
              { label: 'Pending QC', val: pending, color: '#faad14' },
              { label: 'Approved', val: approved, color: '#52c41a' },
              { label: 'On Hold', val: hold, color: '#fa8c16' },
              { label: 'Rejected', val: rejected, color: '#ff4d4f' },
            ].map(s => (
              <Col span={6} key={s.label}>
                <Card size="small">
                  <Space>
                    <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.val}</div>
                    <Text type="secondary">{s.label}</Text>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>

          {pending > 0 && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
              message={`${pending} bale(s) pending HVI testing in this lot`} />
          )}

          <Card>
            <Table rowKey="id" columns={columns} dataSource={baleList} loading={balesLoading}
              size="small" pagination={{ pageSize: 100 }} scroll={{ x: 1100 }}
              rowClassName={(r: any) => r.qc_status === 'REJECTED' ? 'ant-table-row-danger' : ''} />
          </Card>
        </>
      )}

      {/* HVI Entry Drawer */}
      <Drawer
        title={`HVI Entry — Bale ${selectedBale?.bale_no}`}
        open={!!selectedBale}
        onClose={() => setSelectedBale(null)}
        width={540}
        extra={
          <Button type="primary" loading={enterHVI.isPending}
            onClick={() => form.validateFields().then(v =>
              enterHVI.mutate({ bale_id: selectedBale.id, data: v })
            )}>Save HVI</Button>
        }
      >
        {selectedBale && (
          <Descriptions size="small" column={2} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Lot">{activeLot?.mill_lot_no}</Descriptions.Item>
            <Descriptions.Item label="Variety">{activeLot?.cotton_variety || '—'}</Descriptions.Item>
            <Descriptions.Item label="Supplier">{activeLot?.supplier_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Weight">{Number(selectedBale.weight_kg).toFixed(1)} kg</Descriptions.Item>
          </Descriptions>
        )}
        <Form form={form} layout="vertical">
          <Form.Item name="sample_id" label="Sample ID"><Input /></Form.Item>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="micronaire" label="Micronaire"><InputNumber style={{ width: '100%' }} step={0.01} /></Form.Item></Col>
            <Col span={8}><Form.Item name="uhml_mm" label="UHML (mm)"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
            <Col span={8}><Form.Item name="uniformity_index" label="Uniformity %"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="strength_g_tex" label="Strength (g/tex)"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
            <Col span={8}><Form.Item name="elongation_pct" label="Elongation %"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
            <Col span={8}><Form.Item name="sfi" label="SFI"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="rd_value" label="Rd (Reflectance)"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
            <Col span={8}><Form.Item name="plus_b" label="+b (Yellowness)"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
            <Col span={8}><Form.Item name="color_grade" label="Color Grade"><Input placeholder="e.g. 31-3" /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={24}><Form.Item name="trash_pct" label="Trash %"><InputNumber style={{ width: '100%' }} step={0.01} /></Form.Item></Col>
          </Row>
          <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Drawer>

      {/* HVI History Drawer */}
      <Drawer title={`HVI History — ${historyBale?.bale_no}`} open={!!historyBale}
        onClose={() => setHistoryBale(null)} width={700}
      >
        <Table rowKey="id" size="small" dataSource={Array.isArray(hviHistory) ? hviHistory : []}
          columns={[
            { title: 'Date', dataIndex: 'test_date', key: 'dt', width: 130,
              render: (v: string) => dayjs(v).format('DD-MMM-YYYY HH:mm') },
            { title: 'Mic', dataIndex: 'micronaire', key: 'mic', width: 70,
              render: (v: any) => v ? Number(v).toFixed(2) : '—' },
            { title: 'UHML', dataIndex: 'uhml_mm', key: 'uhml', width: 70,
              render: (v: any) => v ? Number(v).toFixed(1) : '—' },
            { title: 'Str', dataIndex: 'strength_g_tex', key: 'str', width: 65,
              render: (v: any) => v ? Number(v).toFixed(1) : '—' },
            { title: 'Rd', dataIndex: 'rd_value', key: 'rd', width: 60,
              render: (v: any) => v ? Number(v).toFixed(1) : '—' },
            { title: '+b', dataIndex: 'plus_b', key: 'pb', width: 60,
              render: (v: any) => v ? Number(v).toFixed(1) : '—' },
            { title: 'Band', dataIndex: 'category_band', key: 'band', width: 75,
              render: (v: string) => v ? <Tag color="blue">{v}</Tag> : '—' },
            { title: 'Status', dataIndex: 'qc_status', key: 'qc', width: 90,
              render: (v: string) => <Tag color={QC_COLORS[v] || 'default'}>{v}</Tag> },
            { title: 'Latest', dataIndex: 'is_latest', key: 'latest', width: 70,
              render: (v: boolean) => v ? <CheckCircleOutlined style={{ color: 'green' }} /> : null },
            {
              title: 'Action', key: 'action', width: 160,
              render: (_: unknown, r: any) => r.is_latest && r.qc_status === 'PENDING' ? (
                <Space>
                  <Button size="small" type="primary"
                    onClick={() => approveHVI.mutate({ result_id: r.id, data: { status: 'APPROVED' } })}>
                    Approve
                  </Button>
                  <Button size="small"
                    onClick={() => approveHVI.mutate({ result_id: r.id, data: { status: 'HOLD' } })}>
                    Hold
                  </Button>
                  <Popconfirm title="Reject this bale?" onConfirm={() =>
                    approveHVI.mutate({ result_id: r.id, data: { status: 'REJECTED' } })
                  }>
                    <Button size="small" danger>Reject</Button>
                  </Popconfirm>
                </Space>
              ) : null,
            },
          ]}
          pagination={false}
        />
      </Drawer>

      {/* Bulk HVI Drawer */}
      <Drawer title={`Bulk HVI — Lot ${activeLot?.mill_lot_no}`} open={bulkOpen}
        onClose={() => setBulkOpen(false)} width={420}
        extra={<Button type="primary" loading={bulkHVI.isPending}
          onClick={() => bulkForm.validateFields().then(v => {
            const rows = (v.rows || []).filter((r: any) => r && r.bale_no)
            bulkHVI.mutate({ lot_id: activeLot.id, rows })
          })}>Submit All</Button>}
      >
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Enter Bale No + key HVI values. System will auto-match to bale in this lot." />
        <Form form={bulkForm} layout="vertical">
          <Form.List name="rows" initialValue={[{}]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => (
                  <Card key={key} size="small" style={{ marginBottom: 8 }}
                    title={<Text code>Row {name + 1}</Text>}
                    extra={<Button size="small" danger onClick={() => remove(name)}>×</Button>}
                  >
                    <Row gutter={8}>
                      <Col span={12}><Form.Item name={[name, 'bale_no']} label="Bale No" rules={[{ required: true }]}><Input placeholder="BL-2026-00001" /></Form.Item></Col>
                      <Col span={12}><Form.Item name={[name, 'micronaire']} label="Mic"><InputNumber style={{ width: '100%' }} step={0.01} /></Form.Item></Col>
                      <Col span={12}><Form.Item name={[name, 'uhml_mm']} label="UHML (mm)"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                      <Col span={12}><Form.Item name={[name, 'strength_g_tex']} label="Str (g/tex)"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                      <Col span={12}><Form.Item name={[name, 'rd_value']} label="Rd"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                      <Col span={12}><Form.Item name={[name, 'plus_b']} label="+b"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                    </Row>
                  </Card>
                ))}
                <Button block onClick={() => add({})} icon={<PlusOutlined />}>Add Row</Button>
              </>
            )}
          </Form.List>
        </Form>
      </Drawer>
    </div>
  )
}
